/**
 * Native push notification bootstrap.
 *
 * When WorldVibe runs inside the Capacitor Android/iOS shell this module:
 *   1. Requests push permission from the OS.
 *   2. Registers with FCM/APNS and receives an opaque device token.
 *   3. POSTs that token to `POST /api/push/register` so the backend can
 *      fan it out through Firebase Admin SDK on new DMs, mentions, gifts.
 *   4. Listens for taps on delivered notifications and deep-links into the
 *      correct in-app route based on the payload's `link` data field.
 *
 * Silent-no-ops on the web build — Web Push is TBD and the module dodges
 * the whole flow if `Capacitor.isNativePlatform() === false`.
 */
import api from './api';

let started = false;

async function loadCapacitor() {
  try {
    const mod = await import('@capacitor/core');
    return mod.Capacitor;
  } catch {
    return null;
  }
}

async function isNative() {
  const Cap = await loadCapacitor();
  return !!Cap?.isNativePlatform?.();
}

async function nativePlatform() {
  const Cap = await loadCapacitor();
  return Cap?.getPlatform?.() || 'web';
}

/**
 * Kicks off the register-with-FCM flow. Safe to call multiple times —
 * subsequent calls are silently ignored.
 *
 * @param {(link: string) => void} navigate  Router `navigate()` for taps.
 */
export async function startPushNotifications(navigate) {
  if (started) return;
  if (!(await isNative())) return;
  started = true;

  let PushNotifications;
  try {
    const mod = await import('@capacitor/push-notifications');
    PushNotifications = mod.PushNotifications;
  } catch (e) {
    console.warn('[push] plugin not available:', e?.message);
    return;
  }

  try {
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== 'granted') {
      console.debug('[push] permission not granted');
      return;
    }

    const platform = await nativePlatform();
    await PushNotifications.register();

    // Token received → send to backend.
    PushNotifications.addListener('registration', async ({ value }) => {
      try {
        await api.post('/push/register', { token: value, platform });
        console.debug('[push] token registered');
      } catch (e) {
        console.warn('[push] register failed', e?.message);
      }
    });

    // Registration error (e.g. no Google Play services on device)
    PushNotifications.addListener('registrationError', (err) => {
      console.warn('[push] registration error', err);
    });

    // Notification received while app is foreground — leave it to LiveNotifier
    // (WebSocket) to show a toast; OS won't show a system notification here.
    PushNotifications.addListener('pushNotificationReceived', () => {});

    // User tapped a notification while app was backgrounded / killed.
    PushNotifications.addListener('pushNotificationActionPerformed', (evt) => {
      const link = evt?.notification?.data?.link;
      if (typeof link === 'string' && link.startsWith('/')) {
        try { navigate(link); } catch { /* noop */ }
      }
    });
  } catch (e) {
    console.warn('[push] bootstrap failed:', e?.message);
  }
}

/**
 * Best-effort revoke on logout. Uses the last-known token stored on window,
 * populated by the `registration` listener above.
 */
export async function stopPushNotifications() {
  if (!(await isNative())) return;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    await PushNotifications.removeAllListeners();
  } catch { /* ignore */ }
  started = false;
}
